import React from 'react'
import styled from 'styled-components'
import {
  Emoji as EmojiData,
  emojiWithName,
  useSendEmoji,
} from 'Event/Dashboard/components/EmojiList/emoji'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import AddEmojiButton from 'Event/Dashboard/components/EmojiList/AddEmojiButton'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {useToggleArray} from 'lib/toggle'
import {EmojiListConfig} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/EmojiList/EmojiListConfig'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import Section from 'Event/template/SimpleBlog/Dashboard/Sidebar/Section'
import {useEditSidebarItem} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem'
import {Ordered} from 'lib/list'
import {useEditMode} from 'Event/EditModeProvider'
import {HasRules} from 'Event/attendee-rules'
import {HasSchedule} from 'lib/ui/layout/Scheduled'

export const EMOJI_LIST = 'Emoji List'
export interface EmojiListProps extends Ordered, HasRules, HasSchedule {
  type: typeof EMOJI_LIST
  emojis: EmojiData['name'][]
  customEmojis?: EmojiData['name'][]
  /**
   * Manually set size of each emoji, if unset
   * each emoji will take up all available
   * space.
   */
  emojiWidth?: number | null
}

export const createEmojiList = (): EmojiListProps => ({
  type: EMOJI_LIST,
  emojis: [],
  customEmojis: [],
  emojiWidth: 60,
})

export default function EmojiList(props: EmojiListProps) {
  const {emojis, emojiWidth} = props
  const isEditMode = useEditMode()

  const [showingConfig, toggleConfig] = useToggleArray()

  const isEmpty = emojis.length === 0
  if (isEmpty) {
    // Add button to create emoji list
    return (
      <EditModeOnly>
        <EmptyConfig
          emojiList={props}
          showingConfig={showingConfig}
          toggleConfig={toggleConfig}
        />
      </EditModeOnly>
    )
  }

  const content = (
    <Box aria-label="emoji list">
      {emojis.map((emoji, index) => (
        <Container key={index} width={emojiWidth}>
          <Emoji emoji={emojiWithName(emoji)} emojiWidth={emojiWidth} />
        </Container>
      ))}
    </Box>
  )

  if (!isEditMode) {
    return <Section>{content}</Section>
  }

  return (
    <ListWithConfig
      emojiList={props}
      showingConfig={showingConfig}
      toggleConfig={toggleConfig}
    >
      {content}
    </ListWithConfig>
  )
}

function EmptyConfig(props: {
  emojiList: EmojiListProps
  showingConfig: boolean
  toggleConfig: () => void
}) {
  const {showingConfig, toggleConfig, emojiList} = props

  return (
    <Section>
      <Config
        emojiList={emojiList}
        showing={showingConfig}
        toggle={toggleConfig}
      />
      <StyledAddEmojiListButton onClick={toggleConfig} />
    </Section>
  )
}

function ListWithConfig(props: {
  emojiList: EmojiListProps
  children: React.ReactElement
  showingConfig: boolean
  toggleConfig: () => void
}) {
  const {showingConfig, toggleConfig, emojiList} = props

  return (
    <Section>
      <Config
        emojiList={emojiList}
        showing={showingConfig}
        toggle={toggleConfig}
      />
      <Editable onEdit={toggleConfig}>{props.children}</Editable>
    </Section>
  )
}

function Config(props: {
  emojiList: EmojiListProps
  showing: boolean
  toggle: () => void
}) {
  const {remove} = useEditSidebarItem()
  const {showing, toggle, emojiList} = props

  return (
    <>
      <EmojiListConfig showing={showing} onClose={toggle} list={emojiList} />
      <RemoveButton size="large" showing onClick={remove}>
        Remove Emojis
      </RemoveButton>
    </>
  )
}

function Emoji(props: {emoji: EmojiData; emojiWidth?: number | null}) {
  const {send, sending} = useSendEmoji()
  const {emoji, emojiWidth} = props

  if (emoji.icon) {
    return (
      <EmojiIcon
        aria-label="event emoji"
        emojiWidth={emojiWidth}
        onClick={send(emoji.name)}
        style={{opacity: sending ? 0.5 : 1}}
      >
        {emoji.icon}
      </EmojiIcon>
    )
  }

  if (emoji.image) {
    return (
      <Image
        aria-label="event emoji"
        src={emoji.image}
        alt={emoji.name}
        onClick={send(emoji.name)}
        style={{opacity: sending ? 0.5 : 1}}
      />
    )
  }

  return null
}

export const EmojiIcon = styled.span<{emojiWidth?: number | null}>`
  font-size: ${(props) => props.emojiWidth}px;
  cursor: pointer;
`

const Box = styled.div`
  padding: 0;
  margin-bottom: ${(props) => props.theme.spacing[2]};
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 5px;
`

const Container = styled((props: any) => {
  const {width: _, ...otherProps} = props
  return <div {...otherProps} />
})`
  margin: 0 ${(props) => props.theme.spacing[3]};
  width: ${(props) => (props.width ? `${props.width}px` : 'auto')};
  display: flex;
  justify-content: center;
  align-items: center;
`

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  cursor: pointer;
`

const StyledAddEmojiListButton = styled(AddEmojiButton)`
  margin-bottom: ${(props) => props.theme.spacing[6]}!important;
`
